/**
 * ms-cart-sub — suscripción dentro del renglón del carrito.
 *
 * Cambia el plan de un renglón con `/cart/change.js` + `selling_plan`
 * (null = compra única). Misma receta que `cart-gift-wrap.js`: pide las
 * secciones de todos los `cart-items-component`, y avisa con CartUpdateEvent;
 * cada componente del carrito se repinta solo con su HTML (lo hace su propio
 * oyente), y el contador, la barra de envío y el resumen se enteran igual que
 * cuando cambia una cantidad.
 *
 * Oyente delegado en `document`: el carrito se morfea en cada cambio y un
 * oyente pegado al nodo se perdería.
 */
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @param {HTMLElement} root - el [data-ms-cart-sub] del renglón
 * @param {string | null} sellingPlan - id del plan, o null para compra única
 * @param {() => void} revertir - regresa el control a como estaba si falla
 */
async function cambiarPlan(root, sellingPlan, revertir) {
  const cartItems = root.closest('cart-items-component');
  const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component')]
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
        .filter(Boolean)
    ),
  ];
  const line = Number(root.dataset.line);

  const body = JSON.stringify({
    line,
    quantity: Number(root.dataset.quantity),
    selling_plan: sellingPlan ? Number(sellingPlan) : null,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  root.setAttribute('aria-busy', 'true');
  cartItems?.classList.add('cart-items-disabled');

  try {
    const response = await fetch(window.Theme?.routes?.cart_change_url ?? '/cart/change.js', fetchConfig('json', { body }));
    const data = JSON.parse(await response.text());

    if (!response.ok || data.errors || data.status) {
      throw new Error(data.errors || data.description || data.message || 'No se pudo cambiar el plan');
    }

    document.dispatchEvent(
      new CartUpdateEvent(data, sectionId ?? 'ms-cart-sub', {
        source: 'ms-cart-sub',
        itemCount: data.item_count,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('ms-cart-sub:', error);
    revertir();
    mostrarError(root, line, error instanceof Error ? error.message : String(error));
  } finally {
    root.removeAttribute('aria-busy');
    cartItems?.classList.remove('cart-items-disabled');
  }
}

/** Usa la celda de error que el tema ya pinta en cada renglón. */
function mostrarError(root, line, mensaje) {
  const row = root.closest('.cart-items__table-row');
  const contenedor = row?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const texto = row?.querySelector(`[ref="cartItemError-${line}"]`);
  if (!contenedor || !texto) return;
  texto.textContent = mensaje;
  contenedor.classList.remove('hidden');
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const root = target.closest('[data-ms-cart-sub]');
  if (!(root instanceof HTMLElement) || root.getAttribute('aria-busy') === 'true') return;

  if (target instanceof HTMLInputElement && target.classList.contains('ms-cart-sub__switch')) {
    const prendido = target.checked;
    cambiarPlan(root, prendido ? root.dataset.plan ?? null : null, () => {
      target.checked = !prendido;
    });
    return;
  }

  if (target instanceof HTMLSelectElement && target.classList.contains('ms-cart-sub__select')) {
    const anterior = [...target.options].find((o) => o.defaultSelected)?.value ?? '';
    cambiarPlan(root, target.value, () => {
      target.value = anterior;
    });
  }
});
